import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { StopPayment } from '@treasury/domain/stop-payments';
import { TmBaseComponent } from '@treasury/presentation';
import { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-currency-field';
import '@treasury/presentation/components/tm-date-picker';
import '@treasury/presentation/components/tm-multi-line-text-input';
import '@treasury/presentation/components/tm-segmented-control';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'editable-stop-payment';
@customElement(tagName)
export class EditableStopPayment extends TmBaseComponent {
    @property({ type: Object })
    public stopPayment!: StopPayment;

    @property({ type: Array })
    public accounts: CompanyAccount[] = [];

    @state()
    private selectedAccount: CompanyAccount | undefined = undefined;

    @state()
    private type: 'range' | 'specific' = 'specific';

    @state()
    private checkNumbers: number[] = [];

    @state()
    private payee: string = '';

    private _remarks: { id: number; updatedDate: string; value: string }[] = [];

    private get remarks() {
        this._remarks = this.stopPayment.remarks.map((item: string, index: number) => {
            return {
                id: index,
                updatedDate: '',
                value: item,
            };
        });
        return this._remarks;
    }

    private set remarks(value: { id: number; updatedDate: string; value: string }[]) {
        this._remarks = value;
    }

    private get stopPaymentType() {
        return this.stopPayment.checkNumberType;
    }

    private set stopPaymentType(value: 'range' | 'specific') {
        this.stopPayment.checkNumberType = value;
        this.type = value;
    }

    private get showOptionsOpenByDefault() {
        const hasRemarks = this.stopPayment.remarks.some((item: string) => {
            if (item) {
                return item.length > 0;
            }
            return false;
        });
        const hasAmount =
            this.stopPayment.checkAmount &&
            this.stopPayment.checkAmount > 0 &&
            this.stopPayment.checkAmount.toString() !== '$0.00';
        return hasAmount || this.stopPayment.checkDate || this.stopPayment.payee || hasRemarks;
    }

    protected render() {
        const checkTypes = [
            { label: 'Specific', value: 'specific' },
            { label: 'Range', value: 'range' },
        ];
        return html`
            <tm-form-row required noWrap .label=${'Account'}>
                <tm-slider
                    header="Select Account"
                    .value=${this.stopPayment.account?.accountDisplayLabel
                        ? html`<div class="flex items-baseline">
                              <span class="text-sm">${this.stopPayment.account?.name}</span
                              ><span class="text-xs text-secondary ml-2"
                                  >${this.stopPayment.account.number}</span
                              >
                          </div>`
                        : undefined}
                    .placeholder=${'Select account'}
                    >${this.renderAccountsList()}</tm-slider
                >
            </tm-form-row>
            <tm-form-row required .label=${''}>
                <div>
                    <span class="text-sm font-medium leading-10">Check number(s)</span><br />
                    <tm-segmented-control
                        .options=${checkTypes}
                        fullWidth
                        .initial=${this.stopPayment.checkNumberType === 'specific'
                            ? checkTypes[0]
                            : checkTypes[1]}
                        @value-changed=${({ detail }: TmValueChanged<SelectItemVm>) => {
                            if (!detail.value) {
                                return;
                            }
                            const value = detail.value.value as 'range' | 'specific';
                            if (!value) {
                                return;
                            }
                            if (value === this.stopPaymentType) {
                                return;
                            }
                            this.stopPaymentType = value;
                            this.type = value;
                            this.stopPayment.checkNumberType = value;
                            this.stopPayment.checkNumbers = [];
                            this.stopPayment.checkDate = '';
                            this.stopPayment.checkAmount = 0;
                            this.stopPayment.payee = '';
                            this.stopPayment.remarks = [''];
                            this.updateStopPayment();
                        }}
                    ></tm-segmented-control>
                    <div class="flex justify-end">
                        ${this.stopPayment.checkNumberType === 'specific'
                            ? html`<tm-text-field
                                  class="max-w-[160px] my-2"
                                  .maxLength=${10}
                                  placeholder="Check number"
                                  .allowedCharacters=${'[0-9]'}
                                  .initial=${this.stopPayment.checkNumbers[0]}
                                  @value-changed=${(e: CustomEvent) => {
                                      this.stopPayment.checkNumbers = [e.detail.value];
                                      this.checkNumbers = [...this.stopPayment.checkNumbers];
                                      this.updateStopPayment();
                                  }}
                              ></tm-text-field>`
                            : html`<div class="flex items-center">
                                  <span class="mr-2 text-secondary text-sm">From</span
                                  ><tm-text-field
                                      class="max-w-[130px] my-2"
                                      .allowedCharacters=${'[0-9]'}
                                      placeholder="Check start"
                                      .maxLength=${10}
                                      .initial=${this.stopPayment.checkNumbers[0]}
                                      @value-changed=${(e: CustomEvent) => {
                                          this.stopPayment.checkNumbers[0] = e.detail.value;
                                          this.checkNumbers = [...this.stopPayment.checkNumbers];
                                          this.updateStopPayment();
                                      }}
                                  ></tm-text-field
                                  ><span class="mx-2 text-secondary text-sm">To</span
                                  ><tm-text-field
                                      class="max-w-[130px] my-2"
                                      .allowedCharacters=${'[0-9]'}
                                      placeholder="Check end"
                                      .maxLength=${10}
                                      .invalid=${Number(this.checkNumbers[1]) <
                                      Number(this.checkNumbers[0])}
                                      .initial=${this.stopPayment.checkNumbers[1]}
                                      @value-changed=${(e: CustomEvent) => {
                                          this.stopPayment.checkNumbers[1] = e.detail.value;
                                          this.checkNumbers = [...this.stopPayment.checkNumbers];
                                          this.updateStopPayment();
                                      }}
                                  ></tm-text-field>
                              </div>`}
                    </div>
                </div>
            </tm-form-row>
            <tm-toggle-content .defaultState=${this.showOptionsOpenByDefault}>
                ${this.renderOptionalFields()}
            </tm-toggle-content>
        `;
    }

    private renderAccountsList() {
        const renderAccount = (item: CompanyAccount) =>
            html` <div class="flex flex-row justify-between py-1">
                <div class="flex flex-col">
                    <div class="text-sm font-medium">${item.name}</div>
                    <div class="text-xs text-secondary">
                        ${item.accountDisplayLabel} | ${item.type}
                    </div>
                </div>
            </div>`;
        return html`<vaadin-grid
               .items=${this.accounts}
               @active-item-changed=${(e: CustomEvent) => {
                   if (!e.detail.value) {
                       return;
                   }
                   this.selectedAccount = e.detail.value;
                   this.stopPayment.account = e.detail.value;

                   e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                   this.updateStopPayment();
               }}
               ><vaadin-grid-column
                   ${columnBodyRenderer(renderAccount as GridColumnBodyLitRenderer<CompanyAccount>)}
               ></vaadin-grid-column
               ></vaadin-grid-column
           ></vaadin-grid>`;
    }

    private renderOptionalFields() {
        return html`${this.stopPaymentType === 'specific'
                ? html`<tm-form-row noWrap .label=${'Check date'}
                          ><tm-date-picker
                              class="max-w-[130px] my-2"
                              .initial=${this.stopPayment.checkDate
                                  ? new Date(this.stopPayment.checkDate)
                                  : undefined}
                              @value-changed=${(e: CustomEvent) => {
                                  const { isValid, value } = e.detail;
                                  if (!isValid || !value) {
                                      return;
                                  }
                                  this.stopPayment.checkDate = value;
                                  this.updateStopPayment();
                              }}
                          ></tm-date-picker></tm-form-row
                      ><tm-form-row noWrap .label=${'Amount'}
                          ><tm-currency-field
                              class="max-w-[130px] my-2"
                              .initial=${this.stopPayment.checkAmount}
                              @value-changed=${(e: TmValueChanged<string>) => {
                                  const { isValid, value } = e.detail;
                                  if (!isValid || !value) {
                                      return;
                                  }

                                  this.stopPayment.checkAmount = parseFloat(value);
                                  this.updateStopPayment();
                              }}
                          ></tm-currency-field></tm-form-row
                      ><tm-form-row noWrap .label=${'Payee'}
                          ><tm-text-field
                              class="my-2"
                              .initial=${this.stopPayment.payee}
                              .maxLength=${25}
                              .helperText=${`${this.stopPayment.payee.length}/25`}
                              @value-changed=${(e: CustomEvent) => {
                                  const { value } = e.detail;
                                  this.payee = value;
                                  this.stopPayment.payee = value;
                                  this.updateStopPayment();
                              }}
                          ></tm-text-field
                      ></tm-form-row>`
                : nothing}<tm-form-row noWrap .label=${'Remarks'}
                ><tm-multi-line-text-input
                    placeholder="Line"
                    .maxLines=${4}
                    .characterLimit=${60}
                    .value=${this.remarks}
                    @change=${(e: CustomEvent) => {
                        this.remarks = e.detail;
                        this.stopPayment.remarks = e.detail.map(
                            (item: { id: number; updatedDate: string; value: string }) => item.value
                        );
                        this.updateStopPayment();
                    }}
                ></tm-multi-line-text-input
            ></tm-form-row>`;
    }

    private updateStopPayment() {
        this.dispatchEvent(new CustomEvent('update', { detail: this.stopPayment }));
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                    --jhd-field-label-width: 150px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: EditableStopPayment;
    }
}
